import { registerNetworkChangeListener } from '../network/onNetworkChange';
import {
  updateActiveSafeNetwork,
  ProbeState,
  clearProbeRetries,
} from './safeNetworksProbe';
import { updateSafeNetworksSettings } from './safeNetworksSettings';
import { getRulesStore, getSafeNetworksStore } from '../storage/stores';
import { isProtectionEnabled } from '../protectionStatus';
import { reportSafeNetworkEvent } from '../api/reportSafeNetworkEvent';
import { ActiveSafeNetwork } from '../types/store/safeNetworks';
import { equals } from 'ramda';
import { getChromeStorage } from '../chrome/wrappers';
import { StoresKeys } from '../types/store';
import { Mutex } from 'async-mutex';
import {
  delegateBySafeNetwork,
  isSafeNetworkDelegated,
  undelegateBySafeNetwork,
} from './safeNetworksDelegation';
import { isDelegationStartedOrNotLoggedInWithDelegation } from '../delegation/delegation';

declare const isDelegation: boolean;

/**
 * Initializes the Safe Networks feature,
 * and fetches settings and updates active Safe Network if it's the first initialization in the session.
 */
export const setupSafeNetworks = async (isFirstInitInSession: boolean) => {
  // Update current SN on every network change.
  registerNetworkChangeListener(updateSafeNetworkStateIfEnabled);

  // Update current SN on every Safe Networks delegation rule change.
  onDelegateProtectionRulesChanged(updateSafeNetworkStateIfEnabled);

  // Fetch SN settings and update current SN on first session initialization.
  if (isFirstInitInSession) {
    await updateSettingsAndActiveSafeNetwork();
  }
};

/**
 * Whether the safe networks feature related code should run or not.
 */
export const canUseSafeNetworkFeature = () => {
  return !isDelegation;
};

/**
 * Checks if the Safe Networks feature should be disabled.
 */
export const shouldDisableSafeNetworksFeature = () => {
  return (
    !canUseSafeNetworkFeature() && getSafeNetworksStore().isFeatureEnabled()
  );
};

export const updateSettingsAndActiveSafeNetwork = async () => {
  await updateSafeNetworksSettings(onSafeNetworkFeatureDisabled);
  await updateSafeNetworkStateIfEnabled();
};

/**
 * Undelegates the protection and reports it when the Safe Networks feature is disabled.
 */
export const onSafeNetworkFeatureDisabled = async () => {
  getSafeNetworksStore().setFeatureEnabled(false);
  await clearProbeRetries();
  const activeSafeNetwork = getSafeNetworksStore().getActiveSafeNetwork();
  const protectionDelegated = isSafeNetworkDelegated();
  if (activeSafeNetwork) {
    getSafeNetworksStore().setActiveSafeNetwork();
    if (protectionDelegated) {
      return undelegateAndReport(activeSafeNetwork);
    }
    return reportLeaving(activeSafeNetwork);
  }
  // This case shouldn't happen because the device can only be delegated when on a SN.
  // But just to be sure we should undelegate if the device is in this state.
  if (protectionDelegated) {
    console.warn('Not in a Safe Network but protection is delegated!');
    return undelegateBySafeNetwork();
  }
};

export const updateSafeNetworkStateIfEnabled = () => {
  if (
    isProtectionEnabled() &&
    isDelegationStartedOrNotLoggedInWithDelegation() &&
    getSafeNetworksStore().isFeatureEnabled()
  ) {
    return updateSafeNetworkState();
  }
};

/**
 * Checks the current Safe Network state, and if needed, it reports it and delegates/undelegates the protection.
 */
const updateSafeNetworkStateMutex = new Mutex();
const updateSafeNetworkState = () =>
  updateSafeNetworkStateMutex.runExclusive(async () => {
    // Save last active Safe Network before updating the current one
    const lastActiveSafeNetwork = getSafeNetworksStore().getActiveSafeNetwork();
    // Check current Safe Network state
    const probeState = await updateActiveSafeNetwork();
    const safeNetworkState = getSafeNetworkState(
      probeState,
      lastActiveSafeNetwork
    );
    console.log(`Safe Network state: ${safeNetworkState.state}`);
    // Report and update protection status according to the Safe Network state
    const shouldDelegateProtection =
      getRulesStore().shouldDelegateProtectionInSafeNetwork();
    const protectionDelegated = isSafeNetworkDelegated();
    if (safeNetworkState.state === 'enteredSafeNetwork') {
      const activeSafeNetwork = safeNetworkState.activeSafeNetwork;
      const changed = await updateAndReportDelegationIfChanged(
        protectionDelegated,
        shouldDelegateProtection,
        activeSafeNetwork
      );
      // If delegation status shouldn't change, and was not already in a SN: Report entering SN.
      if (!changed && !lastActiveSafeNetwork) {
        return reportEntering(activeSafeNetwork);
      }
    } else if (safeNetworkState.state === 'leftSafeNetwork') {
      const lastActiveSafeNetwork = safeNetworkState.lastActiveSafeNetwork;
      if (protectionDelegated) {
        return undelegateAndReport(lastActiveSafeNetwork);
      }
      return reportLeaving(lastActiveSafeNetwork);
    } else if (safeNetworkState.state === 'stillInSafeNetwork') {
      return updateAndReportDelegationIfChanged(
        protectionDelegated,
        shouldDelegateProtection,
        safeNetworkState.activeSafeNetwork
      );
    }
  });

/**
 * If delegation status should change, delegates/undelegates the protection and reports it.
 */
const updateAndReportDelegationIfChanged = (
  protectionDelegated: boolean,
  shouldDelegateProtection: boolean,
  activeSafeNetwork: ActiveSafeNetwork
) => {
  if (!protectionDelegated && shouldDelegateProtection) {
    return delegateAndReport(activeSafeNetwork);
  } else if (protectionDelegated && !shouldDelegateProtection) {
    return undelegateAndReport(activeSafeNetwork);
  }
};

/**
 * Returns the current Safe Network state
 */
const getSafeNetworkState = (
  probeState: ProbeState,
  lastActiveSafeNetwork?: ActiveSafeNetwork
): SafeNetworkState => {
  if (probeState.state === 'inASafeNetwork') {
    const activeSafeNetwork = probeState.activeSafeNetwork;
    return equals(activeSafeNetwork, lastActiveSafeNetwork)
      ? { state: 'stillInSafeNetwork', activeSafeNetwork }
      : { state: 'enteredSafeNetwork', activeSafeNetwork };
  } else if (probeState.state === 'notInASafeNetwork') {
    return lastActiveSafeNetwork === undefined
      ? { state: 'stillOutSafeNetwork' }
      : { state: 'leftSafeNetwork', lastActiveSafeNetwork };
  }
  return lastActiveSafeNetwork === undefined
    ? { state: 'stillOutSafeNetwork' }
    : {
        state: 'stillInSafeNetwork',
        activeSafeNetwork: lastActiveSafeNetwork,
      };
};

const reportEntering = (activeSafeNetwork: ActiveSafeNetwork) =>
  reportSafeNetworkEvent('safeNetworkEntering', activeSafeNetwork);

const reportLeaving = (activeSafeNetwork: ActiveSafeNetwork) =>
  reportSafeNetworkEvent('safeNetworkLeaving', activeSafeNetwork);

const delegateAndReport = async (activeSafeNetwork: ActiveSafeNetwork) => {
  await delegateBySafeNetwork();
  return reportSafeNetworkEvent(
    'safeNetworkDisablingProtection',
    activeSafeNetwork
  );
};

const undelegateAndReport = async (activeSafeNetwork: ActiveSafeNetwork) => {
  await undelegateBySafeNetwork();
  return reportSafeNetworkEvent(
    'safeNetworkEnablingProtection',
    activeSafeNetwork
  );
};

const onDelegateProtectionRulesChanged = (
  callback: () => Promise<void> | undefined
) => {
  getChromeStorage().onChanged.addListener(changes => {
    if (StoresKeys.RULES in changes) {
      const oldDelegateProtectionInSafeNetwork =
        changes[StoresKeys.RULES].oldValue?.safeNetworksRules
          ?.delegateProtectionInSafeNetwork;
      const newDelegateProtectionInSafeNetwork =
        changes[StoresKeys.RULES].newValue?.safeNetworksRules
          ?.delegateProtectionInSafeNetwork;
      if (
        oldDelegateProtectionInSafeNetwork !==
        newDelegateProtectionInSafeNetwork
      ) {
        return callback();
      }
    }
  });
};

type SafeNetworkState =
  | EnteredSafeNetwork
  | LeftSafeNetwork
  | StillInSafeNetwork
  | StillOutSafeNetwork;

interface EnteredSafeNetwork {
  state: 'enteredSafeNetwork';
  activeSafeNetwork: ActiveSafeNetwork;
}

interface LeftSafeNetwork {
  state: 'leftSafeNetwork';
  lastActiveSafeNetwork: ActiveSafeNetwork;
}

interface StillInSafeNetwork {
  state: 'stillInSafeNetwork';
  activeSafeNetwork: ActiveSafeNetwork;
}

interface StillOutSafeNetwork {
  state: 'stillOutSafeNetwork';
}
